/**
 * Component: UserInputArea
 * SpecReferenceID: FRONTEND-WEB-V1
 * Requirements: FRONTEND-WEB-V1-R02, SPEECH-V1-R02, SPEECH-V1-R13, SPEECH-V1-R14
 *
 * Reusable input component supporting both text and optional audio input.
 * When audio is enabled, users can record speech which is transcribed to text.
 *
 * FRONTEND-WEB-V1-R02: System shall collect user input via text or optional audio.
 * SPEECH-V1-R02: Microphone affordance only appears when callers pass
 *                enableAudio=true — call sites gate this on the `speech`
 *                feature flag so the mic is hidden in deployments that do
 *                not subscribe to the Speech module (Bug #4340).
 * SPEECH-V1-R02: The audio button uses a "stop" glyph (not a crossed-out
 *                mic) while actively recording, and reserves the
 *                crossed-out MicOff icon for the genuine permission/
 *                transcription-error state — a crossed-out mic
 *                conventionally means "muted/broken", not "recording"
 *                (Bug #5372).
 *
 * Accessibility: WCAG 2.1 AA compliant
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Mic, MicOff, Square, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { useSpeechToText, type TranscriptionResult } from '@/hooks/use-speech-to-text';
import { useSpeechStreaming } from '@/hooks/use-speech-streaming';
import { useSpeechLanguage } from '@/hooks/use-speech-language';
import { SpeechLanguageSelector } from '@/components/input/SpeechLanguageSelector';
import type { AudioData } from '@/hooks/use-audio-capture';

/**
 * Props for UserInputArea component.
 */
export interface UserInputAreaProps {
  /** Current text value */
  value: string;
  /** Called when text value changes */
  onChange: (value: string) => void;
  /** Called when user submits the input (e.g., presses Enter or clicks submit) */
  onSubmit?: () => void;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Whether the component is in a loading state */
  isLoading?: boolean;
  /** Whether to show audio input option */
  enableAudio?: boolean;
  /** Whether to use textarea (true) or single-line input (false) */
  multiline?: boolean;
  /** ARIA label for the input */
  ariaLabel?: string;
  /** Custom class name for the container */
  className?: string;
  /** Custom class name for the textarea element */
  textareaClassName?: string;
  /** Keyboard shortcut hint (e.g., "Ctrl+Enter to submit") */
  submitHint?: string;
  /** Enable auto-resize for textarea based on content */
  autoResize?: boolean;
  /**
   * Use the real-time streaming speech-to-text path (SPEECH-V1-R07) instead of
   * the batch record-then-transcribe path. Off by default; call sites gate this
   * on the streaming feature flag. The live transcript is display-only
   * (SPEECH-V1-R08); the final transcript populates the input on stop.
   */
  streaming?: boolean;
  /**
   * Bug #5372 follow-up: suppress the built-in "Listening.../Recording..."
   * status row. Use together with `onRecordingStatusChange` when the caller
   * wraps the input in its own bordered container and wants the status text
   * rendered as a sibling *outside* that container instead of appearing to
   * float inside it. Defaults to false (status renders inline, as before).
   */
  hideRecordingStatus?: boolean;
  /**
   * Bug #5372 follow-up: notified with the current recording status
   * whenever it changes (null when not recording), so a caller that sets
   * `hideRecordingStatus` can render the same status text itself, outside
   * its own container.
   */
  onRecordingStatusChange?: (status: RecordingStatusInfo | null) => void;
}

/**
 * Recording status surfaced to callers via `onRecordingStatusChange`.
 * Mirrors the label logic UserInputArea uses for its own internal
 * status row (Bug #5372 follow-up).
 */
export interface RecordingStatusInfo {
  label: string;
}

/**
 * UserInputArea Component
 *
 * FRONTEND-WEB-V1-R02: Collect user input via text or optional audio.
 *
 * @example
 * ```tsx
 * <UserInputArea
 *   value={query}
 *   onChange={setQuery}
 *   onSubmit={handleSearch}
 *   enableAudio={true}
 *   placeholder="Ask a question..."
 * />
 * ```
 */
export function UserInputArea({
  value,
  onChange,
  onSubmit,
  placeholder = 'Enter your query...',
  disabled = false,
  isLoading = false,
  enableAudio = false,
  multiline = false,
  ariaLabel = 'User input',
  className = '',
  textareaClassName = '',
  submitHint,
  autoResize = false,
  streaming = false,
  hideRecordingStatus = false,
  onRecordingStatusChange,
}: UserInputAreaProps): React.ReactElement {
  const [isRecording, setIsRecording] = useState(false);
  const [streamingPending, setStreamingPending] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);
  // Latest value, read inside the streaming onFinal callback to avoid a stale
  // closure that would overwrite earlier segments instead of appending them.
  const valueRef = useRef(value);
  valueRef.current = value;

  /**
   * Handle successful transcription.
   */
  const handleTranscribed = useCallback(
    (result: TranscriptionResult) => {
      if (result.text) {
        // Append transcribed text to existing value
        const newValue = value ? `${value} ${result.text}` : result.text;
        onChange(newValue.trim());
      }
      setIsRecording(false);
    },
    [value, onChange]
  );

  /**
   * Handle transcription error.
   */
  const handleTranscriptionError = useCallback((errorMsg: string) => {
    setAudioError(errorMsg);
    setIsRecording(false);
  }, []);

  // SPEECH-V1-R13/R15: the language the user has declared they will speak,
  // resolved from their stored preference (server for identified users,
  // browser storage otherwise) and falling back to the deployment default.
  // Declared before the transcription hooks because both consume it.
  //
  // Only resolved when the mic is actually available: this component also
  // mounts on surfaces with speech disabled, where fetching the configured
  // set would cost two requests per mount for a control never rendered.
  const {
    language: speechLanguage,
    options: speechLanguageOptions,
    setLanguage: setSpeechLanguage,
  } = useSpeechLanguage({ enabled: enableAudio });

  // Speech-to-text hook for audio transcription
  const {
    transcribe,
    isTranscribing,
    error: transcriptionError,
    clear: clearTranscription,
  } = useSpeechToText({
    // SPEECH-V1-R14: the non-streaming path honours the declared language
    // too, so a deployment with streaming disabled still transcribes in
    // the language the user selected rather than the configured default.
    language: speechLanguage,
    onTranscribed: handleTranscribed,
    onError: handleTranscriptionError,
  });

  // SPEECH-V1-R07/R08: streaming speech-to-text. The live interim transcript is
  // display-only; the final transcript is appended to the input on stop.
  // SPEECH-V1-R14: recognition runs in the declared language.
  const {
    isStreaming,
    interimText,
    start: startStreaming,
    stop: stopStreaming,
  } = useSpeechStreaming({
    language: speechLanguage,
    onFinal: (text) => {
      if (text) {
        // Read the live value via ref so successive segments append rather than
        // overwrite (the callback is bound once at session start).
        const base = valueRef.current;
        const newValue = base ? `${base} ${text}` : text;
        onChange(newValue.trim());
      }
    },
    onError: (msg) => {
      setStreamingPending(false);
      handleTranscriptionError(msg);
    },
  });

  // Clear the optimistic "connecting" flag once the session is live.
  useEffect(() => {
    if (isStreaming) {
      setStreamingPending(false);
    }
  }, [isStreaming]);

  // Stop the mic session as soon as the input is submitted (loading/disabled),
  // so "Listening..." does not persist alongside a generating response. Covers
  // both Enter-to-send and an external send button that drives isLoading.
  useEffect(() => {
    if ((isLoading || disabled) && (isStreaming || streamingPending)) {
      setStreamingPending(false);
      stopStreaming();
    }
  }, [isLoading, disabled, isStreaming, streamingPending, stopStreaming]);

  // Media recorder ref
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  /**
   * Start audio recording.
   */
  const startRecording = useCallback(async () => {
    setAudioError(null);
    clearTranscription();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4',
      });

      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach((track) => track.stop());

        if (audioChunksRef.current.length > 0) {
          const audioBlob = new Blob(audioChunksRef.current, {
            type: mediaRecorder.mimeType,
          });
          // Convert to AudioData format expected by useSpeechToText
          const audioData: AudioData = {
            blob: audioBlob,
            duration: 0, // Duration will be calculated by the service
            mimeType: mediaRecorder.mimeType,
            source: 'microphone',
          };
          // Transcribe the audio
          await transcribe(audioData);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to access microphone. Please check permissions.';
      setAudioError(errorMessage);
    }
  }, [transcribe, clearTranscription]);

  /**
   * Stop audio recording.
   */
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
  }, [isRecording]);

  /**
   * Toggle recording state. In streaming mode this drives the live WebSocket
   * session (SPEECH-V1-R07); otherwise it uses the batch record-then-transcribe
   * path.
   */
  const toggleRecording = useCallback(() => {
    if (streaming) {
      if (isStreaming || streamingPending) {
        setStreamingPending(false);
        stopStreaming();
      } else {
        setAudioError(null);
        setStreamingPending(true);
        void startStreaming();
      }
      return;
    }
    if (isRecording) {
      stopRecording();
    } else {
      void startRecording();
    }
  }, [
    streaming,
    isStreaming,
    streamingPending,
    startStreaming,
    stopStreaming,
    isRecording,
    startRecording,
    stopRecording,
  ]);

  /**
   * Handle key down for submit shortcuts.
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      // For multiline, use Ctrl/Cmd+Enter to submit
      // For single-line, use Enter to submit
      if (multiline) {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          onSubmit?.();
        }
      } else {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          onSubmit?.();
        }
      }
    },
    [multiline, onSubmit]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
      }
      stopStreaming();
    };
  }, [isRecording, stopStreaming]);

  // Auto-resize textarea based on content
  useEffect(() => {
    if (!autoResize || !multiline || !inputRef.current) return;
    const textarea = inputRef.current as HTMLTextAreaElement;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
  }, [value, autoResize, multiline]);

  // Unified "mic active" state across the batch and streaming paths.
  const activeRecording = streaming
    ? isStreaming || streamingPending
    : isRecording;
  const isInputDisabled = disabled || isLoading || isTranscribing;
  const showAudioButton = enableAudio && !isInputDisabled;
  const currentError = audioError ?? transcriptionError;
  const hasAudioError = Boolean(currentError) && !activeRecording;

  // Bug #5372 follow-up: recording-status label, shared by the internal
  // status row and onRecordingStatusChange (for callers that hide the
  // internal row and render this themselves outside their own container).
  const recordingStatusLabel =
    streaming && streamingPending && !isStreaming
      ? 'Connecting...'
      : streaming
        ? 'Listening... Click the microphone to stop.'
        : 'Recording... Click the microphone to stop.';

  useEffect(() => {
    if (!onRecordingStatusChange) return;
    onRecordingStatusChange(activeRecording ? { label: recordingStatusLabel } : null);
  }, [activeRecording, recordingStatusLabel, onRecordingStatusChange]);

  let audioButtonVariant: 'outline' | 'destructive' = 'outline';
  let audioButtonClassName = '';
  let audioButtonAriaLabel = 'Start voice input';
  let audioButtonTitle = 'Start voice input';

  if (activeRecording) {
    audioButtonVariant = 'destructive';
    audioButtonAriaLabel = streaming ? 'Stop voice input' : 'Stop recording';
    audioButtonTitle = audioButtonAriaLabel;
  } else if (hasAudioError) {
    // Bug #4340 Part B: surface permission/transcription errors on the
    // mic button itself (red icon + tooltip) instead of an inline alert
    // banner that crowded the input area.
    audioButtonClassName =
      'text-destructive border-destructive hover:bg-destructive/10 hover:text-destructive';
    audioButtonAriaLabel = `Voice input unavailable: ${currentError ?? ''}`.trim();
    audioButtonTitle = currentError ?? 'Voice input unavailable';
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Input area with optional audio button */}
      <div className="relative flex items-start gap-2">
        {/* Text input */}
        <div className="flex-1 relative">
          {multiline ? (
            <Textarea
              ref={inputRef as React.RefObject<HTMLTextAreaElement>}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={isInputDisabled}
              aria-label={ariaLabel}
              className={`min-h-[80px] resize-none ${
                activeRecording ? 'border-primary animate-pulse' : ''
              } ${textareaClassName}`}
            />
          ) : (
            <Input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              type="text"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={isInputDisabled}
              aria-label={ariaLabel}
              className={activeRecording ? 'border-primary animate-pulse' : ''}
            />
          )}

          {/* Transcribing indicator */}
          {isTranscribing && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="sr-only">Transcribing audio</span>
            </div>
          )}
        </div>

        {/* SPEECH-V1-R13: the active recognition language, shown at the point
            of capture so the user can see what the recogniser expects before
            they speak. Rendered on both the streaming and non-streaming
            paths, since both now forward the declared language. Disabled
            mid-capture because the recognition language is fixed when the
            session or upload begins; changing it would misrepresent what is
            actually being transcribed. */}
        {showAudioButton && (
          <SpeechLanguageSelector
            value={speechLanguage}
            options={speechLanguageOptions}
            onChange={setSpeechLanguage}
            disabled={activeRecording || isTranscribing}
          />
        )}

        {/* Audio button */}
        {showAudioButton && (
          <Button
            type="button"
            variant={audioButtonVariant}
            size="icon"
            onClick={toggleRecording}
            disabled={isTranscribing}
            aria-label={audioButtonAriaLabel}
            aria-pressed={activeRecording}
            title={audioButtonTitle}
            className={audioButtonClassName}
          >
            {activeRecording ? (
              // Bug #5372: a crossed-out mic (MicOff) universally signals
              // "muted / no access / broken" (Zoom/Teams/Meet/Slack, browser
              // permission indicators). Recording is the opposite of that
              // state, so use a "stop" glyph instead - MicOff is reserved
              // below for the genuine hasAudioError branch. Mirrors the
              // Square-while-recording convention already used in
              // MicrophoneCapture.tsx.
              <Square className="h-4 w-4" />
            ) : hasAudioError ? (
              <MicOff className="h-4 w-4" />
            ) : (
              <Mic className="h-4 w-4" />
            )}
          </Button>
        )}
      </div>

      {/* Submit hint */}
      {submitHint && (
        <p className="text-xs text-muted-foreground">{submitHint}</p>
      )}

      {/* Recording indicator. Bug #5372 follow-up: callers that wrap this
          component in their own bordered container can set
          hideRecordingStatus and render this same status (via
          onRecordingStatusChange) outside that container instead. */}
      {activeRecording && !hideRecordingStatus && (
        <div
          className="flex items-center gap-2 text-sm text-primary"
          role="status"
          aria-live="polite"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
          </span>
          {recordingStatusLabel}
        </div>
      )}

      {/* SPEECH-V1-R07/R08: live interim transcript (display-only). Shown while
          streaming; not submitted anywhere until the final transcript is sent. */}
      {streaming && interimText && (
        <p
          className="text-sm text-muted-foreground italic"
          aria-live="polite"
          data-testid="streaming-interim"
        >
          {interimText}
        </p>
      )}
    </div>
  );
}

export default UserInputArea;
